// AD-2 · Reflect On The Move — capture surface (r2).
// Three screens: safety → armed → consent. Read the contract:
// - Amendment 2: hard 90-second ceiling; single stop control.
// - Amendment 3: one non-adaptive prompt shown before recording.
// - Amendment 4: one safety screen, one deliberate confirmation.
// - Amendment 5: NDG makes no motion/driving state claim.
// - Amendment 6: no wake-phrase; no SpeechRecognition.
// - Amendment 7: single 90-second file. IndexedDB local-first.
// - Amendment 8: save (routes through Media) OR discard (client-only).
//
// Recording never begins merely because the AD-1 pathway was chosen —
// the operator must tap "Start 90-second reflection" on the safety screen.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AD2_MAX_DURATION_SECONDS,
  AD2_SAFETY_COPY,
  AD2_START_BUTTON_LABEL,
  AD2_NOT_NOW_BUTTON_LABEL,
  AD2_STOP_BUTTON_LABEL,
  AD2_SAVE_BUTTON_LABEL,
  AD2_DISCARD_BUTTON_LABEL,
  AD2_REFLECTION_PROMPT,
} from "../lib/ad2Copy";
import * as captureStore from "../lib/ad2CaptureStore";
import { speakKarenNarration } from "../utils/karenNarration";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Client-side stop_reason enum (must match backend AD-2 registry).
const STOP_USER_TAP = "user_tap";
const STOP_LENGTH_CEILING = "length_ceiling";

const PHASE_SAFETY = "safety";
const PHASE_ARMED = "armed";
const PHASE_CONSENT = "consent";
const PHASE_SAVED = "saved";     // r2.2 · explicit acknowledged success screen

function uuidv4() {
  // RFC 4122 v4 UUID (crypto-random)
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}


export default function OnTheMoveReflection() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const lessonId = params.get("lesson_id") || "";

  const [phase, setPhase] = useState(PHASE_SAFETY);
  const [elapsed, setElapsed] = useState(0);
  const [captureBlob, setCaptureBlob] = useState(null);
  const [captureMeta, setCaptureMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  // r1 capture-resilience — resilient-storage state (contract:
  // /app/memory/contracts/ad2_capture_resilience_r1.md).
  const [storeAvailable, setStoreAvailable] = useState(true);
  const [crossLessonPresent, setCrossLessonPresent] = useState(false);
  const [expiredRecord, setExpiredRecord] = useState(null);
  const [duplicateLesson, setDuplicateLesson] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const stopReasonRef = useRef(STOP_USER_TAP);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const captureIdRef = useRef(null);
  const promptAbortRef = useRef(null);
  const startingRef = useRef(false);

  // ── Start recording (called on "Start 90-second reflection" tap) ──
  const startRecording = useCallback(async () => {
    if (!lessonId) {
      toast.error("Missing lesson context — cannot start reflection.");
      return;
    }
    if (startingRef.current || mediaRecorderRef.current?.state === "recording") return;
    startingRef.current = true;
    const controller = new AbortController();
    promptAbortRef.current = controller;
    try {
      const spoken = await speakKarenNarration(AD2_REFLECTION_PROMPT, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!spoken) {
        toast.error("Karen's voice is unavailable. Please retry; recording has not started.");
        return;
      }
      // Let the speaker tail finish before requesting the recording microphone.
      await new Promise(resolve => setTimeout(resolve, 650));
      if (controller.signal.aborted) return;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("This device can't currently capture audio in the browser.");
      return;
    }
    if (controller.signal.aborted) { stream.getTracks().forEach(track => track.stop()); return; }

    let recorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
    } catch {
      try {
        recorder = new MediaRecorder(stream);
      } catch {
        stream.getTracks().forEach(track => track.stop());
        toast.error("This device can't currently capture audio in the browser.");
        return;
      }
    }
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
      const durationSeconds = Math.min(
        AD2_MAX_DURATION_SECONDS,
        Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000)),
      );
      const meta = {
        capture_id: captureIdRef.current,
        lesson_id: lessonId,
        duration_seconds: durationSeconds,
        stop_reason: stopReasonRef.current,
        captured_at: new Date(startedAtRef.current).toISOString(),
      };
      setCaptureBlob(blob);
      setCaptureMeta(meta);
      setPhase(PHASE_CONSENT);
      stream.getTracks().forEach((t) => t.stop());
      // r1 capture-resilience — persist the finalized capture. If
      // the store is unavailable (fallback disclosed at safety
      // phase per §9.2) this call is skipped.
      if (storeAvailable) {
        captureStore
          .finalizeCapture({
            capture_id: meta.capture_id,
            blob,
            byte_length: blob.size,
            duration_seconds: durationSeconds,
            stop_reason: meta.stop_reason,
            captured_at: meta.captured_at,
          })
          .catch(() => { /* best-effort; UI already shows the capture */ });
      }
    };
    captureIdRef.current = uuidv4();
    startedAtRef.current = Date.now();
    stopReasonRef.current = STOP_USER_TAP;
    mediaRecorderRef.current = recorder;
    recorder.start();
    setPhase(PHASE_ARMED);
    setElapsed(0);
    // r1 capture-resilience — mark the record `armed` in the
    // store so a mid-recording crash is recoverable via the
    // §7.2 recovery path. Skip when the store is unavailable.
    if (storeAvailable) {
      captureStore
        .beginCapture({
          capture_id: captureIdRef.current,
          lesson_id: lessonId,
          mime: recorder.mimeType || "audio/webm",
        })
        .catch(() => { /* best-effort */ });
    }

    // 90-second ceiling.
    timerRef.current = setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(seconds);
      if (seconds >= AD2_MAX_DURATION_SECONDS) {
        stopReasonRef.current = STOP_LENGTH_CEILING;
        clearInterval(timerRef.current);
        timerRef.current = null;
        try { recorder.stop(); } catch { /* already stopped */ }
      }
    }, 250);
    } finally {
      if (promptAbortRef.current === controller) { promptAbortRef.current = null; startingRef.current = false; }
    }
  }, [lessonId, storeAvailable]);

  // ── Manual stop ──
  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    stopReasonRef.current = STOP_USER_TAP;
    try { mediaRecorderRef.current && mediaRecorderRef.current.stop(); } catch {
      // ignore
    }
  }, []);

  // ── Cleanup on unmount ──
  useEffect(() => () => {
    promptAbortRef.current?.abort();
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    }
  }, []);

  // ── r1 capture-resilience — mount-time recovery ──
  // On mount: probe IndexedDB availability, sweep expired
  // records, and restore any unresolved capture for the current
  // lesson subject to the §5 integrity checks. Cross-lesson
  // records are never silently attached — they are surfaced via
  // an explicit safety-phase notice (§6.1).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const available = await captureStore.isAvailable();
      if (cancelled) return;
      setStoreAvailable(available);
      if (!available) return;
      try {
        await captureStore.sweepExpired();
      } catch { /* best-effort */ }
      let all;
      try {
        all = await captureStore.listUnresolved();
      } catch {
        return;
      }
      if (cancelled) return;
      const forCurrent = all.filter((r) => r.lesson_id === lessonId);
      const forOther = all.filter((r) => r.lesson_id && r.lesson_id !== lessonId);
      setCrossLessonPresent(forOther.length > 0);
      // Prefer an awaiting_choice record for the current lesson.
      const awaiting = forCurrent.find(
        (r) => r.state === captureStore.STATES.AWAITING_CHOICE
              || r.state === captureStore.STATES.SAVING,
      );
      if (awaiting) {
        const check = captureStore.checkIntegrity(awaiting, lessonId);
        if (check.ok) {
          setCaptureBlob(awaiting.blob);
          setCaptureMeta({
            capture_id: awaiting.capture_id,
            lesson_id: awaiting.lesson_id,
            duration_seconds: awaiting.duration_seconds,
            stop_reason: awaiting.stop_reason,
            captured_at: awaiting.captured_at,
          });
          captureIdRef.current = awaiting.capture_id;
          setPhase(PHASE_CONSENT);
          return;
        }
        // Integrity failed — route through explicit recovery UX.
        setExpiredRecord(awaiting);
        return;
      }
      const expired = forCurrent.find(
        (r) => r.state === captureStore.STATES.EXPIRED
              || r.state === captureStore.STATES.ARMED,
      );
      if (expired) {
        setExpiredRecord(expired);
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);

  // ── Save reflection · r2.1 hotfix (Feb 20, 2026) ──
  // Single atomic multipart POST to /api/ad2/reflections that
  // writes both the audio and the reflection row server-side.
  // On any failure the local blob is retained in memory (and the
  // instructor is shown a meaningful, actionable recovery
  // message) rather than being discarded and replaced by the
  // raw upstream 404 that appeared in production Feb 20, 2026.
  const doSave = useCallback(async () => {
    if (!captureBlob || !captureMeta) return;
    setSaving(true);
    setDuplicateLesson(false);
    // r1 capture-resilience — mark the record `saving` before the
    // network call. The record is NOT deleted on network failure;
    // it is deleted only on confirmed server persistence (§10.2).
    if (storeAvailable) {
      try { await captureStore.beginSave(captureMeta.capture_id); } catch { /* best-effort */ }
    }
    try {
      const form = new FormData();
      form.append("file", captureBlob, `ad2-${captureMeta.capture_id}.webm`);
      form.append("capture_id",       captureMeta.capture_id);
      form.append("lesson_id",        captureMeta.lesson_id);
      form.append("duration_seconds", String(captureMeta.duration_seconds));
      form.append("stop_reason",      captureMeta.stop_reason);
      form.append("captured_at",      captureMeta.captured_at);
      const token = localStorage.getItem("access_token") || "";
      const res = await axios.post(`${API}/ad2/reflections`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status < 200 || res.status >= 300 || !res.data) {
        throw new Error("Save did not complete.");
      }
      // Success: confirmed server persistence. Only NOW may we
      // delete the local record and clear the in-memory state.
      if (storeAvailable) {
        try { await captureStore.confirmSave(captureMeta.capture_id); } catch { /* best-effort */ }
      }
      setCaptureBlob(null);
      chunksRef.current = [];
      // captureMeta is retained so the SAVED phase can render
      // the confirmed duration and lesson association.
      setPhase(PHASE_SAVED);
      toast.success("Reflection saved successfully.");
    } catch (err) {
      // r1 §10.4 — a 409 means the server already has a canonical
      // reflection for this lesson. Route to the explicit
      // "duplicate for this lesson" UX; do NOT silently discard.
      const status = err && err.response && err.response.status;
      if (status === 409) {
        setDuplicateLesson(true);
      } else if (storeAvailable) {
        // r1 §10.2 step 5 — any other non-2xx / network error
        // reverts to awaiting_choice. Record and blob are RETAINED.
        try { await captureStore.failSave(captureMeta.capture_id); } catch { /* best-effort */ }
      }
      // Meaningful recovery message. The blob and metadata are
      // deliberately RETAINED so the instructor can retry the
      // save without re-recording. Never surface a raw upstream
      // "Not Found" to the operator.
      const recovery =
        "We couldn't upload your reflection just now. It is still safely captured on this device — try Save again in a moment.";
      if (status !== 409) toast.error(recovery);
      // Keep captureBlob and captureMeta populated. Do NOT
      // navigate. Do NOT clear the consent screen. Frontend
      // remains in awaiting-route state so Save is retryable.
    } finally {
      setSaving(false);
    }
  }, [captureBlob, captureMeta, storeAvailable]);

  // ── Discard reflection (client-side only — leaves no trace) ──
  const doDiscard = useCallback(() => {
    // r1 capture-resilience — delete the local record on Discard.
    if (storeAvailable && captureMeta && captureMeta.capture_id) {
      captureStore.discard(captureMeta.capture_id).catch(() => { /* best-effort */ });
    }
    setCaptureBlob(null);
    setCaptureMeta(null);
    setDuplicateLesson(false);
    chunksRef.current = [];
    navigate("/work-mode");
  }, [navigate, storeAvailable, captureMeta]);

  // ── r1 capture-resilience — expired-record recovery handlers ──
  const doExpiredRecover = useCallback(() => {
    if (!expiredRecord) return;
    const check = captureStore.checkIntegrity(expiredRecord, lessonId);
    if (!check.ok) {
      // Blob/metadata failed integrity: cannot recover; discard.
      captureStore.discard(expiredRecord.capture_id).catch(() => {});
      setExpiredRecord(null);
      toast.error("The stored reflection could not be recovered.");
      return;
    }
    // Integrity passed — restore into consent phase for the user
    // to choose Save or Discard. (Post-recovery the record is
    // treated as awaiting_choice.)
    captureStore
      .finalizeCapture({
        capture_id: expiredRecord.capture_id,
        blob: expiredRecord.blob,
        byte_length: expiredRecord.byte_length,
        duration_seconds: expiredRecord.duration_seconds,
        stop_reason: expiredRecord.stop_reason,
        captured_at: expiredRecord.captured_at,
      })
      .catch(() => {});
    setCaptureBlob(expiredRecord.blob);
    setCaptureMeta({
      capture_id: expiredRecord.capture_id,
      lesson_id: expiredRecord.lesson_id,
      duration_seconds: expiredRecord.duration_seconds,
      stop_reason: expiredRecord.stop_reason,
      captured_at: expiredRecord.captured_at,
    });
    captureIdRef.current = expiredRecord.capture_id;
    setExpiredRecord(null);
    setPhase(PHASE_CONSENT);
  }, [expiredRecord, lessonId]);

  const doExpiredDiscard = useCallback(() => {
    if (!expiredRecord) return;
    captureStore.discard(expiredRecord.capture_id).catch(() => {});
    setExpiredRecord(null);
  }, [expiredRecord]);

  // ── r1 §10.4 — duplicate-for-lesson: discard local copy only ──
  const doDuplicateDiscard = useCallback(() => {
    if (storeAvailable && captureMeta && captureMeta.capture_id) {
      captureStore.discard(captureMeta.capture_id).catch(() => {});
    }
    setCaptureBlob(null);
    setCaptureMeta(null);
    setDuplicateLesson(false);
    chunksRef.current = [];
    navigate("/work-mode");
  }, [navigate, storeAvailable, captureMeta]);

  const elapsedLabel = useMemo(() => {
    const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const ss = String(elapsed % 60).padStart(2, "0");
    return `${mm}:${ss} / 01:30`;
  }, [elapsed]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="ad2-page">
      <div className="max-w-md mx-auto px-4 py-8 space-y-6">
        {/* r1 capture-resilience — expired-record recovery gate.
            When an older unfinished reflection for THIS lesson
            exists, present an explicit Recover / Discard choice
            (§7.3). No timeout, no default action, no silent
            deletion. This block takes priority over every phase. */}
        {expiredRecord && (
          <div data-testid="ad2-expired-recovery" className="space-y-6">
            <h1 className="text-2xl font-semibold">Older reflection found</h1>
            <p
              data-testid="ad2-consent-expired-recovery-note"
              className="text-slate-300 leading-relaxed"
            >
              You have an older unfinished reflection from this lesson. Choose Recover to open it, or Discard to remove it from this device.
            </p>
            <div className="space-y-3 pt-4">
              <button
                type="button"
                data-testid="ad2-expired-recover-btn"
                onClick={doExpiredRecover}
                className="w-full py-6 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white text-lg font-semibold"
              >
                Recover
              </button>
              <button
                type="button"
                data-testid="ad2-expired-discard-btn"
                onClick={doExpiredDiscard}
                className="w-full py-6 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-lg font-semibold"
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {!expiredRecord && phase === PHASE_SAFETY && (
          <div data-testid="ad2-safety" className="space-y-6">
            <h1 className="text-2xl font-semibold">Reflect on the move</h1>
            <p data-testid="ad2-safety-copy" className="text-slate-300 leading-relaxed">
              {AD2_SAFETY_COPY}
            </p>
            <div className="border-t border-slate-800 pt-6 space-y-3">
              <p className="text-sm text-slate-400">Reflection prompt</p>
              <p data-testid="ad2-safety-prompt" className="text-lg leading-relaxed">
                {AD2_REFLECTION_PROMPT}
              </p>
            </div>
            <div className="space-y-3 pt-4">
              <button
                type="button"
                data-testid="ad2-safety-start-btn"
                onClick={startRecording}
                className="w-full py-6 rounded-2xl bg-sky-600 hover:bg-sky-500 text-white text-lg font-semibold"
              >
                {AD2_START_BUTTON_LABEL}
              </button>
              <button
                type="button"
                data-testid="ad2-safety-not-now-btn"
                onClick={() => navigate("/work-mode")}
                className="w-full py-6 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-lg font-semibold"
              >
                {AD2_NOT_NOW_BUTTON_LABEL}
              </button>
            </div>
            {/* r1 capture-resilience §9.2 — pre-recording disclosure
                when IndexedDB is unavailable. Learner is told
                honestly that this reflection will not survive
                refresh, navigation, or browser closure this
                session. Never suppress; never silently fall back. */}
            {!storeAvailable && (
              <p
                data-testid="ad2-safety-resilience-unavailable-note"
                className="text-[11px] text-amber-400 text-center pt-2"
              >
                {"Your browser can't safely keep this reflection between visits. If you refresh, navigate away, or close this tab before choosing Save or Discard, the reflection will be lost."}
              </p>
            )}
            {/* r1 capture-resilience §6.1 — cross-lesson notice.
                A capture from another lesson exists on this
                device. It is NEVER silently attached to the
                current lesson — the learner must open that lesson
                to Save or Discard it. */}
            {crossLessonPresent && (
              <p
                data-testid="ad2-safety-cross-lesson-notice"
                className="text-[11px] text-slate-400 text-center pt-1"
              >
                An unfinished reflection from another lesson is safely kept on this device. Open that lesson to Save or Discard it.
              </p>
            )}
          </div>
        )}

        {!expiredRecord && phase === PHASE_ARMED && (
          <div data-testid="ad2-armed" className="space-y-8">
            <div className="text-center space-y-3">
              <div
                data-testid="ad2-recording-indicator"
                className="inline-flex items-center gap-3 px-6 py-3 bg-red-900/30 border border-red-500/50 rounded-full"
              >
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-200 text-sm font-medium">Recording</span>
              </div>
              <p data-testid="ad2-elapsed-time" className="text-5xl font-mono">
                {elapsedLabel}
              </p>
              <p className="text-xs text-slate-500">{AD2_REFLECTION_PROMPT}</p>
            </div>
            <button
              type="button"
              data-testid="ad2-stop-btn"
              onClick={stopRecording}
              className="w-full py-6 rounded-2xl bg-red-600 hover:bg-red-500 text-white text-lg font-semibold"
            >
              {AD2_STOP_BUTTON_LABEL}
            </button>
          </div>
        )}

        {!expiredRecord && phase === PHASE_CONSENT && captureMeta && (
          <div data-testid="ad2-consent" className="space-y-6">
            <h1 className="text-2xl font-semibold">Reflection captured</h1>
            <p data-testid="ad2-consent-duration" className="text-slate-300">
              Duration: {String(Math.floor(captureMeta.duration_seconds / 60)).padStart(2, "0")}
              :{String(captureMeta.duration_seconds % 60).padStart(2, "0")}
            </p>
            <div className="space-y-3 pt-4">
              <button
                type="button"
                data-testid="ad2-consent-save-btn"
                onClick={doSave}
                disabled={saving}
                className="w-full py-6 rounded-2xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-lg font-semibold"
              >
                {saving ? "Saving…" : AD2_SAVE_BUTTON_LABEL}
              </button>
              <button
                type="button"
                data-testid="ad2-consent-discard-btn"
                onClick={doDiscard}
                disabled={saving}
                className="w-full py-6 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-200 text-lg font-semibold"
              >
                {AD2_DISCARD_BUTTON_LABEL}
              </button>
            </div>
            <p className="text-xs text-slate-500 text-center pt-2">
              You choose. NDG only records what you route.
            </p>
            {captureBlob && (
              <p
                data-testid="ad2-consent-local-safety-note"
                className="text-[11px] text-slate-500 text-center pt-1"
              >
                Your reflection is safely captured on this device until you choose.
              </p>
            )}
            <p
              data-testid="ad2-consent-save-effect-note"
              className="text-[11px] text-slate-500 text-center pt-1"
            >
              Save keeps this reflection in your NDG account. Discard removes it from this device.
            </p>
            {/* r1 capture-resilience §10.4 — the server returned
                409: a reflection for this lesson is already saved.
                Offer explicit "discard local copy" — never
                silently delete. */}
            {duplicateLesson && (
              <div className="pt-4 space-y-3">
                <p
                  data-testid="ad2-consent-duplicate-lesson-note"
                  className="text-[12px] text-amber-400 text-center"
                >
                  A reflection for this lesson is already saved in your NDG account. You can discard this local copy.
                </p>
                <button
                  type="button"
                  data-testid="ad2-consent-duplicate-discard-btn"
                  onClick={doDuplicateDiscard}
                  className="w-full py-4 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold"
                >
                  Discard local copy
                </button>
              </div>
            )}
          </div>
        )}

        {!expiredRecord && phase === PHASE_SAVED && captureMeta && (
          <div data-testid="ad2-saved" className="space-y-6">
            <div className="flex items-center justify-center pt-4">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500 flex items-center justify-center">
                <span className="text-emerald-400 text-4xl">✓</span>
              </div>
            </div>
            <div className="text-center space-y-2">
              <h1
                data-testid="ad2-saved-heading"
                className="text-2xl font-semibold text-emerald-300"
              >
                Reflection saved successfully.
              </h1>
              <p data-testid="ad2-saved-lesson-line" className="text-slate-300">
                {"Added to today's lesson."}
              </p>
              <p className="text-xs text-slate-500 pt-2">
                Duration recorded:{" "}
                {String(Math.floor(captureMeta.duration_seconds / 60)).padStart(2, "0")}
                :{String(captureMeta.duration_seconds % 60).padStart(2, "0")}
              </p>
            </div>
            <div className="space-y-3 pt-6">
              <button
                type="button"
                data-testid="ad2-saved-done-btn"
                onClick={() => {
                  setCaptureMeta(null);
                  navigate("/work-mode");
                }}
                className="w-full py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white text-lg font-semibold"
              >
                Done
              </button>
            </div>
            <p className="text-[11px] text-slate-500 text-center">
              Your saved reflections are visible in your Professional Passport.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

